import React from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaLock, FaComments, FaClipboardCheck } from 'react-icons/fa';

const Booking = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const features = [
    {
      icon: <FaComments />,
      title: "Confidential Space",
      description: "All discussions remain completely private in a judgment-free environment."
    },
    {
      icon: <FaCalendarAlt />,
      title: "Flexible Scheduling",
      description: "Choose a time that works best for your schedule."
    },
    {
      icon: <FaClipboardCheck />,
      title: "Personalised Approach",
      description: "Each session is tailored to your specific goals and challenges."
    }
  ];

  return (
    <BookingContainer id="booking">
      <div className="container">
        <BookingContent ref={ref}>
          <BookingHeader>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.5 }}
            >
              <SectionTitle>Book Your Coaching Session</SectionTitle>
              <SectionSubtitle>Take the first step towards transforming your potential into performance</SectionSubtitle>
            </motion.div>
          </BookingHeader>
          
          <BookingColumns>
            <BookingInfo>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <IntroText>
                  Schedule a discovery call to discuss your goals and see if we're the right fit for each other.
                </IntroText>
                
                <FeaturesList>
                  {features.map((feature, index) => (
                    <FeatureItem 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ duration: 0.4, delay: 0.4 + (index * 0.1) }}
                    >
                      <FeatureIcon>{feature.icon}</FeatureIcon>
                      <FeatureContent>
                        <FeatureTitle>{feature.title}</FeatureTitle>
                        <FeatureDescription>{feature.description}</FeatureDescription>
                      </FeatureContent>
                    </FeatureItem>
                  ))}
                </FeaturesList>
                
                <SecureNotice>
                  <FaLock /> Your information is kept secure and confidential
                </SecureNotice>
              </motion.div>
            </BookingInfo>
            
            <CalendlyWrapper>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <CalendlyContainer>
                  {/* Replace with actual Calendly embed code */}
                  <iframe 
                    src="https://calendly.com/samanthaknowles-info/30min" 
                    width="100%" 
                    height="650" 
                    frameBorder="0"
                    title="Schedule a call with Samantha"
                  ></iframe>
                </CalendlyContainer>
              </motion.div>
            </CalendlyWrapper>
          </BookingColumns>
        </BookingContent>
      </div>
      <BackgroundElement className="element-1" />
      <BackgroundElement className="element-2" />
      <DiagonalDivider />
    </BookingContainer>
  );
};

const BookingContainer = styled.section`
  background-color: white;
  position: relative;
  overflow: hidden;
  padding: 100px 0 120px;
`;

const BookingContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
`;

const BookingHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 20px;
  position: relative;
  color: #1A2A3A;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #A8C4A0;
  }
`;

const SectionSubtitle = styled.p`
  color: #5A7F8C;
  font-size: 1.2rem;
  line-height: 1.6;
  max-width: 650px;
  margin: 30px auto 0;
`;

const BookingColumns = styled.div`
  display: flex;
  gap: 50px;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const BookingInfo = styled.div`
  flex: 0.4;
  
  @media (max-width: 992px) {
    order: 2;
  }
`;

const IntroText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #555;
  margin-bottom: 30px;
`;

const FeaturesList = styled.div`
  margin-bottom: 30px;
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  margin-bottom: 25px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(168, 196, 160, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #5A7F8C;
  margin-right: 15px;
  flex-shrink: 0;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h4`
  font-size: 1.1rem;
  color: #1A2A3A;
  margin-bottom: 8px;
`;

const FeatureDescription = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
`;

const SecureNotice = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #888;
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  
  svg {
    margin-right: 8px;
    color: #A8C4A0;
  }
`;

const CalendlyWrapper = styled.div`
  flex: 0.6;
  
  @media (max-width: 992px) {
    order: 1;
    margin-bottom: 40px;
  }
`;

const CalendlyContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(168, 196, 160, 0.2);
  height: 650px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -20px;
    right: -20px;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background-color: rgba(90, 127, 140, 0.05);
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -30px;
    left: -30px;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background-color: rgba(168, 196, 160, 0.05);
    z-index: -1;
  }
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const BackgroundElement = styled.div`
  position: absolute;
  
  &.element-1 {
    width: 600px;
    height: 600px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v50H50v50H0z' fill='%23A8C4A0' fill-opacity='0.05'/%3E%3C/svg%3E");
    top: -200px;
    right: -200px;
    z-index: 0;
    transform: rotate(-15deg);
  }
  
  &.element-2 {
    width: 500px;
    height: 500px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h50v50H0z' fill='%235A7F8C' fill-opacity='0.03'/%3E%3C/svg%3E");
    bottom: -100px;
    left: -150px;
    z-index: 0;
    transform: rotate(10deg);
  }
`;

const DiagonalDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-color: #f9f9f9;
  clip-path: polygon(0 100%, 100% 40%, 100% 100%, 0 100%);
  z-index: 1;
`;

export default Booking;